<div [hidden]="!ViewReady">
    <h1 class="mat-h1">
        Out Of Office Settings<span *ngIf="ActiveOutOfOffice">
            - <span class="accentbackground200">Active Designation In Effect</span></span
        >
    </h1>
    <div>
        The following tools let you configure your IRIS "Out Of Office" settings. The "Out Of Office" settings allow you to designate
        one or more individuals who may act on your behalf to handle requests forwarded to you by an IRIS facilitator while you are out
        of the office and cannot respond on your own. You may use the tools provided below to specify the individual you wish to
        designate to handle your requests, along with the timeframe you wish to use for this designation.
    </div>
    <br />
    <div><strong>Note: </strong> Please note the following:</div>
    <ul>
        <li>
            While an "Out of Office" designation is in effect, the individual(s) designated by you to act on behalf will be emailed
            with a copy of any email notifications you would normally receive for any requests.
        </li>
        <li>
            "Out of Office" designations are only in effect for the time periods listed below. If a time period expires, the
            designation will immediately end and the designated individual will no longer have access to requests forwarded to
            yourself.
        </li>
        <li>
            You may leave the End Date for an "Out of Office" designation empty, and this will create an open-ended "Out of Office"
            designation. You will need to deactivate or remove it at a later time in order to stop the designation, however.
        </li>
        <li>
            For a designation which you may often use in the future, you can opt to "Deactivate" the designation rather than "Remove"
            it. This process turns off the designation, but leaves the entry in the table below for you to quickly "Reactivate" at a
            later time with updated dates.
        </li>
    </ul>
    <div [class.centered]="SmallDisplay">
        <button type="button" mat-raised-button color="primary" (click)="ShowOutOfOfficeForm()">
            Add New Out Of Office Designation
        </button>
    </div>
    <table
        class="fullwidth"
        mat-table
        [dataSource]="OutOfOfficeSettingsSource"
        matSort
        matSortActive="DesigneeLastName"
        matSortDirection="asc"
    >
        <ng-container matColumnDef="DesigneeLastName">
            <th mat-header-cell mat-sort-header *matHeaderCellDef>Designee Last</th>
            <td mat-cell *matCellDef="let des">
                <div>{{ des.DesigneeLastName | titleCase }}</div>
            </td>
        </ng-container>

        <ng-container matColumnDef="DesigneeFirstName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Designee First</th>
            <td mat-cell *matCellDef="let des">
                <div>{{ des.DesigneeFirstName | titleCase }}</div>
            </td>
        </ng-container>

        <ng-container matColumnDef="DesigneeSAPID">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>SAP #</th>
            <td mat-cell *matCellDef="let des">
                <div>{{ des.DesigneeSAPID }}</div>
            </td>
        </ng-container>

        <ng-container matColumnDef="DesigneeLocalJobTitle">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Title</th>
            <td mat-cell *matCellDef="let des">
                <div>{{ des.DesigneeLocalJobTitle }}</div>
            </td>
        </ng-container>

        <ng-container matColumnDef="Categories">
            <th mat-header-cell *matHeaderCellDef class="centered">Categories</th>
            <td mat-cell *matCellDef="let des">
                <div fxLayout="column" fxLayoutAlign="start center">
                    <div *ngFor="let cat of des.Categories">{{ cat.CategoryDescription }}</div>
                </div>
            </td>
        </ng-container>

        <ng-container matColumnDef="DateBegin">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="centered">Begin Date</th>
            <td mat-cell *matCellDef="let des" class="centered">
                <div>{{ des.DateBegin | date: 'M/d/yy' }}</div>
            </td>
        </ng-container>

        <ng-container matColumnDef="DateEnd">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="centered">End Date</th>
            <td mat-cell *matCellDef="let des" class="centered">
                <div *ngIf="des.DateEnd">{{ des.DateEnd | date: 'M/d/yy' }}</div>
                <div *ngIf="!des.DateEnd">N/A (Open)</div>
            </td>
        </ng-container>

        <ng-container matColumnDef="Active">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="centered">Active?</th>
            <td mat-cell *matCellDef="let des" class="centered">
                <div>{{ des.Active | yesNo }}</div>
            </td>
        </ng-container>

        <ng-container matColumnDef="Actions">
            <th mat-header-cell *matHeaderCellDef class="centered">Action(s)</th>
            <td mat-cell *matCellDef="let des">
                <div fxLayout="row" fxLayoutAlign="center center" fxLayoutGap="15px">
                    <button
                        type="button"
                        *ngIf="des.Active === true || !des.InWindow"
                        mat-raised-button
                        color="primary"
                        (click)="EditDesignee(des)"
                    >
                        Update
                    </button>
                    <button
                        type="button"
                        *ngIf="des.Active === true && des.InWindow"
                        mat-raised-button
                        color="accent"
                        (click)="DeactivateDesignee(des, false)"
                    >
                        Deactivate
                    </button>
                    <button type="button" mat-raised-button color="warn" (click)="DeactivateDesignee(des, true)">Remove</button>
                    <button
                        type="button"
                        *ngIf="des.Active !== true && des.InWindow"
                        mat-raised-button
                        color="accent"
                        (click)="ReactivateDesignee(des)"
                    >
                        Reactivate
                    </button>
                </div>
            </td>
        </ng-container>

        <ng-container matColumnDef="NoData">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let des" [attr.colspan]="VisibleHeaders?.length">
                <em>There are no "Out of Office" designations in the IRIS system for you at this time.</em>
            </td>
        </ng-container>

        <ng-container matColumnDef="SmallDisplay">
            <th mat-header-cell *matHeaderCellDef class="centered">"Out of Office" Designations</th>
            <td mat-cell *matCellDef="let des">
                <div fxLayout="column" fxLayoutAlign="start stretch">
                    <div fxLayout="row" fxLayoutAlign="space-between start">
                        <div>Name</div>
                        <div>
                            <span>{{ des.DesigneeFirstName | titleCase }} {{ des.DesigneeLastName | titleCase }}</span>
                        </div>
                    </div>
                    <!-- <div fxLayout="row" fxLayoutAlign="space-between start">
                        <div>SAP #</div>
                        <div>{{ des.DesigneeSAPID }}</div>
                    </div> -->
                    <div fxLayout="row" fxLayoutAlign="space-between start">
                        <div>Title</div>
                        <div>{{ des.DesigneeLocalJobTitle }}</div>
                    </div>
                    <div fxLayout="row" fxLayoutAlign="space-between start">
                        <div>Categories</div>
                        <div fxLayout="column" fxLayoutAlign="start end">
                            <div *ngFor="let cat of des.Categories">{{ cat.CategoryDescription }}</div>
                        </div>
                    </div>
                    <div fxLayout="row" fxLayoutAlign="space-between start">
                        <div>Begin Date</div>
                        <div>{{ des.DateBegin | date: 'M/d/yy' }}</div>
                    </div>
                    <div fxLayout="row" fxLayoutAlign="space-between start">
                        <div>End Date</div>
                        <div *ngIf="des.DateEnd">{{ des.DateEnd | date: 'M/d/yy' }}</div>
                        <div *ngIf="!des.DateEnd">N/A (Open)</div>
                    </div>
                    <div fxLayout="row" fxLayoutAlign="space-between start">
                        <div>Active?</div>
                        <div>{{ des.Active | yesNo }}</div>
                    </div>
                    <div fxLayout="column" fxLayoutAlign="start center" fxLayoutGap="15px" class="margin-top margin-bottom">
                        <button
                            type="button"
                            *ngIf="des.Active === true"
                            mat-raised-button
                            color="accent"
                            (click)="DeactivateDesignee(des, false)"
                        >
                            Deactivate
                        </button>
                        <button
                            type="button"
                            *ngIf="des.Active === true"
                            mat-raised-button
                            color="warn"
                            (click)="DeactivateDesignee(des, true)"
                        >
                            Remove
                        </button>
                        <button
                            type="button"
                            *ngIf="des.Active !== true"
                            mat-raised-button
                            color="accent"
                            (click)="ReactivateDesignee(des)"
                        >
                            Reactivate
                        </button>
                    </div>
                </div>
            </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="VisibleHeaders"></tr>
        <tr
            mat-row
            *matRowDef="let row; columns: VisibleColumns; let $even = even"
            [class.primarybackground50]="$even && !!row.Active"
            [class.warnbackground50]="!row.Active"
        ></tr>
    </table>

    <mat-paginator
        [pageSizeOptions]="[5, 10, 20, 50]"
        [pageSize]="20"
        [length]="OutOfOfficeSettingsSource?.data?.length"
    ></mat-paginator>
</div>
